import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';

// Scoped event queries let Firestore enforce campaign ownership, including old events.
export function useEmailCampaignData(user, viewAll) {
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    setCampaigns([]);
    setEvents([]);
    setError(null);
    setLoading(true);
    if (!user?.uid) { setLoading(false); return undefined; }
    let active = true;
    let revision = 0;
    let eventUnsubs = [];
    const fail = err => {
      if (!active) return;
      console.error('Failed to load email campaign data:', err);
      setError(err);
      setEvents([]);
      setLoading(false);
    };
    const source = collection(db, collectionPath('emailCampaigns'));
    const unsubscribe = onSnapshot(viewAll ? source : query(source, where('createdBy', '==', user.uid)), snap => {
      if (!active) return;
      const currentRevision = ++revision;
      const rows = snap.docs.map(d => ({ ...d.data(), id: d.id })).sort((a, b) =>
        (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setCampaigns(rows);
      eventUnsubs.forEach(stop => stop());
      setEvents([]);
      const eventsByCampaign = new Map();
      const eventSource = collection(db, collectionPath('emailEvents'));
      if (viewAll) {
        eventUnsubs = [onSnapshot(eventSource, eventSnap => {
          if (!active || revision !== currentRevision) return;
          setEvents(eventSnap.docs.map(d => ({ ...d.data(), id: d.id })));
          setLoading(false);
        }, fail)];
      } else {
        const pending = new Set(rows.map(row => row.id));
        if (!pending.size) setLoading(false);
        eventUnsubs = rows.map(row => onSnapshot(query(eventSource, where('campaignId', '==', row.id)), eventSnap => {
          if (!active || revision !== currentRevision) return;
          eventsByCampaign.set(row.id, eventSnap.docs.map(d => ({ ...d.data(), id: d.id })));
          setEvents([...eventsByCampaign.values()].flat());
          pending.delete(row.id);
          if (!pending.size) setLoading(false);
        }, fail));
      }
    }, fail);
    return () => { active = false; unsubscribe(); eventUnsubs.forEach(stop => stop()); };
  }, [user?.uid, viewAll]);
  return { campaigns, events, loading, error };
}
